/**
 * F29 (Wave 2C) — Admin teams queue page.
 *
 * Route: /teams
 * Permission: teams:read (super_admin + support + content_moderator)
 *
 * Table view of all teams across the platform — any status (active /
 * suspended / disbanded). Status filter chips above the table (mirrors F11's
 * VenueClaimsQueuePage chip pattern). Name search via SearchInput.
 *
 * No pending-count badge in the sidenav — per D11, F29 has NO admin queue:
 * moderation is reactive (a member reports → admin investigates). The page is
 * a search + browse surface, not a work queue.
 *
 * Row click → /teams/:teamId detail page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chip,
  EmptyState,
  SearchInput,
  Skeleton,
  Stat,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { AdminPageMeta, TeamStatus } from '@bravemove/types';
import { adminTeamsApi, type AdminTeamRow, type AdminTeamsSearchOpts } from '../../api/teams.js';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';

const PAGE_LIMIT = 25;
type StatusFilter = TeamStatus | 'all';
const DEFAULT_STATUS: StatusFilter = 'active';

const FILTER_CHIPS: ReadonlyArray<{ value: StatusFilter; labelKey: string }> = [
  { value: 'active', labelKey: 'admin:team.admin.queue.filter_active' },
  { value: 'suspended', labelKey: 'admin:team.admin.queue.filter_suspended' },
  { value: 'disbanded', labelKey: 'admin:team.admin.queue.filter_disbanded' },
  { value: 'all', labelKey: 'admin:team.admin.queue.filter_all' },
];

const STATUS_CHIP_MAP: Record<TeamStatus, StatusChipStatus> = {
  active: 'approved',
  suspended: 'in_review',
  disbanded: 'rejected',
};

/** Build a sparse list-opts object so undefined keys aren't serialised. */
function buildSearchOpts(raw: {
  status: StatusFilter;
  q: string | null;
  page: number;
}): AdminTeamsSearchOpts {
  const opts: AdminTeamsSearchOpts = { perPage: PAGE_LIMIT, page: raw.page };
  if (raw.status && raw.status !== 'all') {
    return { ...opts, status: raw.status, ...(raw.q ? { q: raw.q } : {}) };
  }
  return { ...opts, ...(raw.q ? { q: raw.q } : {}) };
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminTeamsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = (searchParams.get('status') as StatusFilter | null) ?? DEFAULT_STATUS;
  const qParam = searchParams.get('q');
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);
  const [qDraft, setQDraft] = useState<string>(qParam ?? '');

  // KPI counts — the server returns global per-status totals (`statusCounts`)
  // on every search response, so no second fetch is needed (TGS-02; the old
  // `limit: 500` KPI fetch 422'd against the route's limit ≤ 50 cap).
  const [kpiCounts, setKpiCounts] = useState<{
    active: number;
    suspended: number;
    disbanded: number;
  } | null>(null);

  const [rows, setRows] = useState<readonly AdminTeamRow[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: AdminTeamsSearchOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminTeamsApi.search(opts);
        setRows(data.data);
        setPagination(data.pagination ?? null);
        if (data.statusCounts) setKpiCounts(data.statusCounts);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(buildSearchOpts({ status: statusParam, q: qParam, page: pageParam }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam, qParam, pageParam]);

  // Debounce the search input → URL → effect chain.
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = qDraft.trim();
      const current = qParam ?? '';
      if (trimmed === current) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        next.delete('page');
        return next;
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [qDraft, qParam, setSearchParams]);

  const goToPage = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  const onStatusChange = (value: StatusFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_STATUS) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const columns = useMemo<readonly TableColumn<AdminTeamRow>[]>(
    () => [
      {
        key: 'name',
        header: t('admin:team.admin.queue.column_name'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-[13px] text-ink">{row.name}</span>
            {row.homeCazaCode ? (
              <span className="font-mono text-[11.5px] text-ink-3">{row.homeCazaCode}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'sport',
        header: t('admin:team.admin.queue.column_sport'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {t(`sports:${row.sportName}`, { defaultValue: row.sportName })}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('admin:team.admin.queue.column_status'),
        // Defensive guard (TGS-01): if a row arrives without a known status
        // (older server build / partial row), render a dash instead of letting
        // StatusChip dereference an undefined tone and crash the whole table.
        cell: (row) =>
          row.status && STATUS_CHIP_MAP[row.status] ? (
            <StatusChip
              size="sm"
              status={STATUS_CHIP_MAP[row.status]}
              pulse={row.status === 'suspended'}
              label={t(`team:status.${row.status}`)}
            />
          ) : (
            <span className="text-[12px] text-ink-3">—</span>
          ),
      },
      {
        key: 'privacy',
        header: t('admin:team.admin.queue.column_privacy'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">{t(`team:privacy.${row.privacy}`)}</span>
        ),
      },
      {
        key: 'members',
        header: t('admin:team.admin.queue.column_members'),
        cell: (row) => (
          <span className="tabular-nums text-[12.5px] text-ink">{row.memberCount}</span>
        ),
      },
      {
        key: 'captain',
        header: t('admin:team.admin.queue.column_captain'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink">{row.captainDisplayName ?? '—'}</span>
        ),
      },
      {
        key: 'created',
        header: t('admin:team.admin.queue.column_created'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">{formatDate(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:team.admin.queue_title')}
        subtitle={t('admin:team.admin.queue_subtitle')}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3" data-testid="teams-kpi-strip">
        <Stat
          label={t('admin:team.admin.queue.kpi.active')}
          value={kpiCounts === null ? '—' : kpiCounts.active}
        />
        <Stat
          label={t('admin:team.admin.queue.kpi.suspended')}
          value={kpiCounts === null ? '—' : kpiCounts.suspended}
        />
        <Stat
          label={t('admin:team.admin.queue.kpi.disbanded')}
          value={kpiCounts === null ? '—' : kpiCounts.disbanded}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Status filter chips */}
        <div
          role="tablist"
          aria-label={t('admin:team.admin.queue.filter_active')}
          className="flex flex-wrap items-center gap-2"
        >
          {FILTER_CHIPS.map((chip) => (
            <Chip
              key={chip.value}
              selected={statusParam === chip.value}
              onClick={() => onStatusChange(chip.value)}
            >
              {t(chip.labelKey)}
            </Chip>
          ))}
        </div>

        <SearchInput
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
          placeholder={t('admin:team.admin.queue.search_placeholder')}
          aria-label={t('admin:team.admin.queue.search_placeholder')}
          data-testid="teams-queue-search"
        />
      </div>

      {error ? (
        <div data-testid="teams-queue-error">
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}

      {loading ? (
        <div className="space-y-3" data-testid="teams-queue-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="team" size={36} color="currentColor" />}
          title={t('admin:team.admin.queue.empty_title')}
          description={t('admin:team.admin.queue.empty_body')}
        />
      ) : (
        <Table
          data={rows as AdminTeamRow[]}
          columns={columns as TableColumn<AdminTeamRow>[]}
          rowKey={(row) => row.id}
          onRowPress={(row) => navigate(`/teams/${row.id}`)}
          empty={t('admin:team.admin.queue.empty_title')}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="admin-teams-pagination"
        />
      ) : null}
    </div>
  );
}
